import {RouteDefinition} from '../../routes';
import {matchPath, Route, Switch, useLocation} from 'react-router-dom';
import {Header} from '../Header';
import {Sidebar} from '../Sidebar';
import {MenuItem} from '../MenuItem';
import {Footer} from '../Footer';
import {CallScreen} from '../../screens/CallScreen';
import {PopupScreen} from '../../screens/PopupScreen';
import React, {memo, ReactNode, useCallback, useEffect, useState} from 'react';
import {Viewport} from '../Viewport';
import {ScenarioCreateScreen} from '../../screens/ScenarioCreateScreen';

import './index.scss';
import {ScenarioPopupScreen} from '../../screens/ScenarioPopupScreen';
import {MediaScreen} from '../../screens/MediaScreen';
import {ScenarioTriggerCreateScreen} from '../../screens/ScenarioTriggerCreateScreen';
import {ScenarioTriggerPopupScreen} from '../../screens/ScenarioTriggerPopupScreen';
import {useRPC} from '../../hooks/rpc';
import {ScenarioTriggerControlUpdateScreen} from '../../screens/ScenarioTriggerControlUpdateScreen';
import {NotFound404} from '../NotFound404';
import {BusyIndicator} from '../BusyIndicator';
import {MenuEntity} from '../../store/control/state';
import {leftMenuIconList} from '../../screens/Settings/LeftMenu/LeftMenuEditScreen';
import {useBackground} from '../../hooks/background-class';
import {StpUIMediaScreen} from '../../screens/StpUIMediaScreen';
import {onClickHandler} from "../../utils/onclick";

type Props = {
    busy: boolean;
    routes: RouteDefinition[];
    leftMenuList: MenuEntity[];
    locked: boolean;
    lockedReason: string;
    isPc: boolean;
    scenarioCreateScreenVisible: boolean;
    scenarioTriggerCreateScreenVisible: boolean;
    scenarioTriggerControlUpdateScreenVisible: boolean;
    mediaScreenVisible: boolean;
    popupScreenVisible: boolean;
    applicationLoaded: boolean;
    stpUIMediaScreenVisible: boolean;
};
type ReactNodeOrRenderer = (() => ReactNode);

declare function renderIf(predicate: any): (item: ReactNodeOrRenderer, elseValue?: ReactNodeOrRenderer) => ReactNode;

export const AppComponent = memo(function AppComponent(props: Props) {
    const {
        routes,
        leftMenuList,
        busy,
        locked,
        lockedReason,
        isPc,
        applicationLoaded,
        scenarioCreateScreenVisible,
        scenarioTriggerCreateScreenVisible,
        scenarioTriggerControlUpdateScreenVisible,
        mediaScreenVisible,
        popupScreenVisible,
        stpUIMediaScreenVisible,
    } = props;

    const popupVisible = scenarioCreateScreenVisible || mediaScreenVisible || stpUIMediaScreenVisible || scenarioTriggerCreateScreenVisible || scenarioTriggerControlUpdateScreenVisible;
    const [sidebarVisible, setSidebarVisible] = useState(true);
    const [, , backgroundStyle] = useBackground();

    const location = useLocation();
    const [rpc, isConnected, retryCount] = useRPC();

    const touchEventHandler = useCallback(() => {
        rpc.touchEvent().then();
    },[rpc]);

    useEffect(() => {
        const match = matchPath(location.pathname, {
            path: '/settings',
            exact: false,
            strict: false,
        });
        const match2 = matchPath(location.pathname, {
            path: '/developer',
            exact: false,
            strict: false,
        });
        setSidebarVisible(isConnected && match == null && match2 == null);

    }, [location, isConnected]);

    return (
        <div
            className={'main' + (popupVisible ? ' popupVisible' : '') + (isPc ? ' pc' : '')} style={backgroundStyle} {...onClickHandler(touchEventHandler)}>
            <Header locked={locked}/>
            {
                renderIf(locked)(() =>
                    <div className='locked-reason-container'>
                        <span>{lockedReason}</span>
                    </div>,
                )
            }
            {
                renderIf(sidebarVisible)(() =>
                    <Sidebar locked={locked}>
                        {
                            leftMenuList.map(leftMenu => {
                                return (
                                    <MenuItem
                                        key={leftMenu.name}
                                        routerLink={leftMenu.is_special ? `/${leftMenu.id}` : `/page/${leftMenu.id}`}
                                        exact={true}
                                        title={leftMenu.name}
                                        onClick={leftMenu.onClick}
                                        iconUrl={leftMenuIconList.find(l => l.value.name === leftMenu.icon)?.value?.iconUrl}/>
                                );
                            })
                        }
                    </Sidebar>,
                )
            }

            <Viewport isConnected={isConnected} retryCount={retryCount}>
                {
                    renderIf(applicationLoaded)(() =>
                        <Switch>
                            {
                                routes.filter(route => route.routerLink).map(
                                    route => (
                                        <Route
                                            key={route.routerLink}
                                            path={route.routerLink}
                                            exact={true}
                                            component={route.component}/>
                                    ),
                                )
                            }
                            <Route
                                path='*'
                                exact={true}
                                component={NotFound404}/>
                        </Switch>,
                    )
                }
            </Viewport>
            <Footer locked={locked}/>

            <CallScreen/>
            {renderIf(popupScreenVisible)(() => <PopupScreen/>)}
            <ScenarioPopupScreen/>
            <ScenarioTriggerPopupScreen/>
            {renderIf(scenarioCreateScreenVisible)(() => <ScenarioCreateScreen/>)}
            {renderIf(scenarioTriggerCreateScreenVisible)(() => <ScenarioTriggerCreateScreen/>)}
            {renderIf(scenarioTriggerControlUpdateScreenVisible)(() => <ScenarioTriggerControlUpdateScreen/>)}
            {renderIf(mediaScreenVisible)(() => <MediaScreen/>)}
            {renderIf(stpUIMediaScreenVisible)(() => <StpUIMediaScreen/>)}
            {renderIf(busy)(() => <BusyIndicator/>)}
        </div>
    );
});
